import { BADGES } from "../data/badges";
import { WEEKMAP } from "../data/sessions";
import { cycleOn } from "./cycle";
import { DAY_MS, DOW_LABELS, parseDay, todayStr, weekDays, weekKey } from "./date";
import { dayKcal, daySteps } from "./energy";
import { beforePlan, neutralDay, sessionKeyFor, sessionsPerWeek, weekNo } from "./plan";
import { programmeFor } from "./programme";
import { sessionPct } from "./queue";
import { etaToTarget, ewma, trendRate, weightSeries } from "./trend";
import type { Badge, BadgeCtx, KayaState, SessionKey, Stats } from "./types";

export function badgeCtx(S: KayaState): BadgeCtx {
  return {
    logs: S.logs,
    cycle: S.cycle,
    weekNo: weekNo(S),
    dayFull: (ds: string) => dayFull(S, ds),
    weekDays: sessionsPerWeek(S),
  };
}

/** Anything at all happened on this day — enough to keep a streak alive. */
export function dayActive(S: KayaState, ds: string): boolean {
  const L = S.logs[ds];
  return !!(
    L &&
    (L.frozen ||
      L.rescued ||
      L.periodSkip ||
      (L.extras && L.extras.length) ||
      (L.done && Object.values(L.done).some((o) => Object.keys(o).length)) ||
      L.treadmill)
  );
}

/** The session was actually finished, not merely started. */
export function dayFull(S: KayaState, ds: string): boolean {
  const L = S.logs[ds];
  if (!L) return false;
  const key = L.sessionKey || sessionKeyFor(S, ds);
  const pct = sessionPct(S, ds, key);
  return !!(L.completedAt || (pct !== null && pct >= 1));
}

/** Scheduled days in the last three with nothing logged, oldest first, excluding today. A day
 *  before the plan's start was never scheduled, so it is never missed — nor frozen, since
 *  `applyFreezes` spends only on what this returns. */
export function missedDays(S: KayaState): string[] {
  const out: string[] = [];
  const d = new Date();
  for (let i = 1; i <= 3; i++) {
    d.setDate(d.getDate() - 1);
    const ds = todayStr(d);
    if (neutralDay(S, ds) || beforePlan(S, ds)) continue;
    if (!dayActive(S, ds)) out.push(ds);
  }
  return out.reverse();
}

export function streak(S: KayaState): number {
  let n = 0;
  const d = new Date();
  for (let i = 0; i < 400; i++) {
    const ds = todayStr(d);
    const isRest = neutralDay(S, ds);
    if (dayActive(S, ds)) n++;
    else if (!isRest && i > 0) break;
    d.setDate(d.getDate() - 1);
  }
  return n;
}

export function longestStreak(S: KayaState): number {
  const days = Object.keys(S.logs)
    .filter((ds) => dayActive(S, ds))
    .sort();
  let best = 0;
  let cur = 0;
  let prev: string | null = null;

  days.forEach((ds) => {
    if (prev) {
      const d = parseDay(prev);
      let ok = true;
      // every day between the two must be one the streak is allowed to skip
      for (;;) {
        d.setDate(d.getDate() + 1);
        const x = todayStr(d);
        if (x === ds) break;
        if (!neutralDay(S, x)) {
          ok = false;
          break;
        }
      }
      cur = ok ? cur + 1 : 1;
    } else cur = 1;
    best = Math.max(best, cur);
    prev = ds;
  });
  return best;
}

export function weekCount(S: KayaState): number {
  return weekDays().filter((ds) => dayFull(S, ds)).length;
}

export function stats(S: KayaState): Stats {
  const days = Object.keys(S.logs);
  const full = days.filter((ds) => dayFull(S, ds));
  const tread = days.filter((ds) => S.logs[ds].treadmill);
  const doubles = days.filter((ds) => dayFull(S, ds) && S.logs[ds].treadmill);

  const ms = S.measures.slice().sort((a, b) => (a.date < b.date ? -1 : 1));
  const fw = ms.find((m) => m.w);
  const lw = ms.slice().reverse().find((m) => m.w);
  const fwa = ms.find((m) => m.waist);
  const lwa = ms.slice().reverse().find((m) => m.waist);

  const early = days.some((ds) => {
    const L = S.logs[ds];
    return !!L.completedAt && new Date(L.completedAt).getHours() < 8;
  });
  const travel = days.some((ds) => S.logs[ds].sessionKey === "T" && dayFull(S, ds));

  const comeback = (() => {
    const f = full.slice().sort();
    for (let i = 1; i < f.length; i++)
      if ((parseDay(f[i]).getTime() - parseDay(f[i - 1]).getTime()) / DAY_MS >= 4) return true;
    return false;
  })();

  const bTypes = new Set(full.map((ds) => S.logs[ds].sessionKey || sessionKeyFor(S, ds)));

  const byWeek: Record<string, number> = {};
  full.forEach((ds) => {
    const k = weekKey(parseDay(ds));
    byWeek[k] = (byWeek[k] || 0) + 1;
  });
  // a clean week is every session of the person's own week — six in the default map (ADR-050)
  const perWeek = Math.max(1, sessionsPerWeek(S));
  const fullWeeks = Object.values(byWeek).filter((n) => n >= perWeek).length;

  const kcal = days.reduce((a, ds) => a + dayKcal(S, ds), 0);

  return {
    sessions: full.length,
    treadmills: tread.length,
    treadMin: tread.length * 30,
    doubles: doubles.length,
    streak: streak(S),
    longest: longestStreak(S),
    weekCount: weekCount(S),
    fullWeeks,
    early,
    travel,
    comeback,
    weighIns: ms.filter((m) => m.w).length,
    waistIns: ms.filter((m) => m.waist).length,
    lost: fw && lw ? fw.w! - lw.w! : 0,
    waistLost: fwa && lwa ? fwa.waist! - lwa.waist! : 0,
    phase: (S.profile.phaseMode !== "auto"
      ? +S.profile.phaseMode
      : weekNo(S) <= 2
        ? 0
        : weekNo(S) <= 4
          ? 1
          : 2) as Stats["phase"],
    allTypes: allTypesFor(S).every((k) => bTypes.has(k)),
    kcal,
  };
}

/**
 * The "Full set" badge's three sessions: A, B and C on the Kaya programme; Strength & balance,
 * Gentle cardio and the Chair session on Steady. `data/badges.ts` is generated and reads
 * `s.allTypes`, so the programme is read here. (ADR-052)
 */
export function allTypesFor(S: KayaState): SessionKey[] {
  return S.profile.training && programmeFor(S) === "steady" ? ["SB", "GC", "CH"] : ["A", "B", "C"];
}

/** Badges newly satisfied but not yet recorded in state. Callers persist and celebrate them. */
export function freshBadges(S: KayaState, st?: Stats): Badge[] {
  const s = st || stats(S);
  const ctx = badgeCtx(S);
  return BADGES.filter((b) => !S.badges[b.id] && b.need(s, ctx));
}

/** The unearned badge the user is closest to, for the "next up" nudge. */
export function nextBadge(S: KayaState): { b: Badge; a: number; n: number; r: number } | null {
  const st = stats(S);
  const ctx = badgeCtx(S);
  let best: { b: Badge; a: number; n: number; r: number } | null = null;

  BADGES.forEach((b) => {
    if (S.badges[b.id]) return;
    if ((b.id === "cycle1" || b.id === "gentle3" || b.id === "care5") && !cycleOn(S)) return;
    const [a, n] = b.prog(st, ctx);
    const r = a / n;
    if (!best || r > best.r || (r === best.r && n < best.n)) best = { b, a, n, r };
  });
  return best;
}

export function streakLine(n: number): string {
  if (n >= 30) return "Thirty-plus days. This is who you are now.";
  if (n >= 14) return "Two weeks straight.";
  if (n >= 7) return "A full week in a row.";
  if (n >= 3) return "Three in a row — momentum.";
  if (n >= 1) return "Streak started.";
  return "Start a streak today.";
}

export interface WeekStats {
  days: string[];
  sessions: number;
  tread: number;
  kcal: number;
  min: number;
  steps: number;
}

/** `offset` 0 = this week, 1 = last week, counted back from `now`. */
export function weekStats(S: KayaState, offset: number, now: Date = new Date()): WeekStats {
  const t = new Date(now);
  t.setDate(t.getDate() - 7 * offset);
  const days = weekDays(t);
  const L = (ds: string) => S.logs[ds] || ({} as KayaState["logs"][string]);

  return {
    days,
    sessions: days.filter((ds) => dayFull(S, ds)).length,
    tread: days.filter((ds) => L(ds).treadmill).length,
    kcal: days.reduce((a, ds) => a + dayKcal(S, ds), 0),
    min:
      Math.round(days.reduce((a, ds) => a + (L(ds).elapsedMs || 0), 0) / 60000) +
      days.filter((ds) => L(ds).treadmill).length * 30,
    steps: days.reduce((a, ds) => a + daySteps(S, ds), 0),
  };
}

export interface Insights {
  consistency: number;
  best: string;
  bestN: number;
  avgMin: number;
  totalMin: number;
  proj: { perWeek: number; date: Date | null; daysLeft?: number } | null;
}

/** 28-day rollup plus the trend-weight projection. */
export function insights(S: KayaState): Insights {
  const days: string[] = [];
  for (let i = 0; i < 28; i++) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    days.push(todayStr(d));
  }

  const sched = days.filter((ds) => sessionKeyFor(S, ds) !== "R" && ds !== todayStr());
  const done = sched.filter((ds) => dayActive(S, ds));
  const consistency = sched.length ? Math.round((done.length / sched.length) * 100) : 0;

  const byDow = [0, 0, 0, 0, 0, 0, 0];
  Object.keys(S.logs).forEach((ds) => {
    if (dayFull(S, ds)) byDow[parseDay(ds).getDay()]++;
  });
  const mx = Math.max(...byDow);
  const best = byDow.indexOf(mx);
  const ties = byDow.filter((v) => v === mx).length;

  const act = Object.values(S.logs).filter((L) => L.elapsedMs);
  const avgMin = act.length
    ? Math.round(act.reduce((a, L) => a + (L.elapsedMs || 0), 0) / act.length / 60000)
    : 0;
  const totalMin =
    Math.round(Object.values(S.logs).reduce((a, L) => a + (L.elapsedMs || 0), 0) / 60000) +
    stats(S).treadMin;

  // The smoothed trend over the recent window, not a fit through every weigh-in since day one:
  // that let the first fortnight's water loss set the pace forever. `perWeek` stays positive
  // for a loss, as callers expect.
  const trend = ewma(weightSeries(S));
  const rate = trendRate(trend, 21, todayStr());
  let proj: Insights["proj"] = null;
  if (rate !== null) {
    const eta = etaToTarget(S, trend[trend.length - 1].v, rate);
    proj = eta
      ? { perWeek: -rate, date: parseDay(eta.date), daysLeft: eta.days }
      : { perWeek: -rate, date: null };
  }

  return {
    consistency,
    best: ties > 1 ? "tied" : DOW_LABELS[best],
    bestN: mx,
    avgMin,
    totalMin,
    proj,
  };
}

export { WEEKMAP };

/** The weekly recap is offered on Monday and Tuesday, once, after a week with a session in it. */
export function recapDue(S: KayaState, now: Date): boolean {
  const dow = now.getDay();
  if (dow !== 1 && dow !== 2) return false;
  if (S.recapSeen[weekKey(now)]) return false;
  return weekStats(S, 1, now).sessions > 0;
}
